import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { formatEuro } from "../lib/format";
import {
  ArrowDownCircle, ArrowUpCircle, Wallet, Target, Sparkles, TrendingDown,
  ArrowUp, ArrowDown,
} from "lucide-react";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  AreaChart, Area, ResponsiveContainer, XAxis, YAxis, CartesianGrid, Tooltip,
  ReferenceLine, BarChart, Bar, Legend,
} from "recharts";
import StatusBadge from "../components/StatusBadge";

const RANGES = [
  { value: "12", label: "12 mo" },
  { value: "24", label: "24 mo" },
  { value: "60", label: "5 yr" },
  { value: "120", label: "All" },
];

const formatMonthShort = (ym) => {
  if (!ym) return "";
  const [y, m] = ym.split("-");
  const d = new Date(Number(y), Number(m) - 1, 1);
  return d.toLocaleDateString("en-GB", { month: "short", year: "2-digit" });
};

const NUMERIC_DEBT_KEYS = new Set([
  "debt_left", "planned_monthly_payment", "average_monthly_interest",
  "interest_this_month", "total_monthly_cost",
]);
const NUMERIC_PROJ_KEYS = new Set([
  "month_index", "payment_this_month", "interest_this_month",
  "net_change", "total_debt", "active_debts",
]);

function useSort(initialKey, initialDir = "desc") {
  const [sortBy, setSortBy] = useState(initialKey);
  const [sortDir, setSortDir] = useState(initialDir);
  const toggle = (key, numericKeys) => {
    if (sortBy === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(key);
      setSortDir(numericKeys.has(key) ? "desc" : "asc");
    }
  };
  return { sortBy, sortDir, toggle };
}

export default function PlanPage() {
  const [summary, setSummary] = useState(null);
  const [projection, setProjection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [months, setMonths] = useState("24");

  const debtSort = useSort("total_monthly_cost", "desc");
  const projSort = useSort("month_index", "asc");

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [s, p] = await Promise.all([
          api.get("/plan/summary"),
          api.get("/plan/projection", { params: { months } }),
        ]);
        setSummary(s.data);
        setProjection(p.data);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [months]);

  const totals = summary?.totals || {};
  const items = summary?.items || [];
  const timeline = projection?.timeline || [];

  const sortedItems = useMemo(() => {
    const dir = debtSort.sortDir === "asc" ? 1 : -1;
    const get = (row) => {
      const v = row[debtSort.sortBy];
      if (typeof v === "string") return v.toLowerCase();
      return v ?? 0;
    };
    return [...items].sort((a, b) => {
      const va = get(a), vb = get(b);
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    });
  }, [items, debtSort.sortBy, debtSort.sortDir]);

  const sortedProjection = useMemo(() => {
    const rows = timeline.slice(1);
    const dir = projSort.sortDir === "asc" ? 1 : -1;
    const get = (row) => {
      if (projSort.sortBy === "month") return row.month || "";
      return row[projSort.sortBy] ?? 0;
    };
    return [...rows].sort((a, b) => {
      const va = get(a), vb = get(b);
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    });
  }, [timeline, projSort.sortBy, projSort.sortDir]);

  const empty = !loading && items.length === 0;

  return (
    <div className="space-y-8 fade-in">
      <div>
        <p className="label-kicker">Strategy</p>
        <h1 className="font-display text-4xl sm:text-5xl font-medium tracking-tight text-[#1A1C1A] mt-2">
          Payment plan
        </h1>
        <p className="text-[#5C635A] mt-2 max-w-2xl">
          Your monthly commitment per debt, the resulting total expenses, and a forward look at how
          the debt is planned to melt away — using your planned payment and average interest per debt.
        </p>
      </div>

      {empty ? (
        <div className="card-soft p-10 text-center">
          <Target className="w-10 h-10 mx-auto text-[#8B9389]" strokeWidth={1.5} />
          <h3 className="font-display text-2xl text-[#1D3328] mt-4">No active debts</h3>
          <p className="text-[#5C635A] mt-2 max-w-md mx-auto">
            Add a debt and (optionally) set its <em>planned monthly payment</em> and{" "}
            <em>average monthly interest</em> to see your payment plan come to life.
          </p>
          <Link
            to="/debts"
            className="inline-block mt-5 text-[#2C4A3B] font-medium underline underline-offset-4"
          >
            Go to debts →
          </Link>
        </div>
      ) : (
        <>
          {/* Aggregate tiles */}
          <section className="grid grid-cols-2 lg:grid-cols-4 gap-5 stagger">
            <Tile
              icon={<ArrowDownCircle className="w-4 h-4" />}
              label="Planned payments"
              value={formatEuro(totals.planned_payments || 0)}
              sub={`across ${totals.active_debts || 0} active debts`}
              accent="#2C4A3B"
              testId="plan-total-payments"
            />
            <Tile
              icon={<ArrowUpCircle className="w-4 h-4" />}
              label="Interest this month"
              value={formatEuro(totals.interest_this_month || 0)}
              sub="absorbed inside your payments"
              accent="#C89B6A"
              testId="plan-total-interest"
            />
            <Tile
              icon={<Wallet className="w-4 h-4" />}
              label="Total monthly outflow"
              value={formatEuro(totals.monthly_outflow || 0)}
              sub="what actually leaves your wallet"
              accent="#1D3328"
              testId="plan-total-outflow"
            />
            <Tile
              icon={<TrendingDown className="w-4 h-4" />}
              label="Net monthly reduction"
              value={formatEuro(totals.net_monthly_reduction || 0)}
              sub={
                (totals.net_monthly_reduction || 0) > 0
                  ? "debt actually shrinking"
                  : "interest outpaces payments"
              }
              accent={(totals.net_monthly_reduction || 0) > 0 ? "#2C4A3B" : "#B04A32"}
              testId="plan-net-reduction"
            />
          </section>

          {/* Per-debt table */}
          <section className="card-soft p-6">
            <p className="label-kicker">Per debt</p>
            <h3 className="font-display text-xl text-[#1D3328] mt-1 mb-4">Monthly payment plans</h3>
            <div className="overflow-x-auto -mx-2">
              <table className="w-full text-sm" data-testid="plan-debts-table">
                <thead>
                  <tr className="text-left label-kicker border-b border-[#EEEBE2]">
                    <SortableTh sort={debtSort} k="code" numericKeys={NUMERIC_DEBT_KEYS}>Code</SortableTh>
                    <SortableTh sort={debtSort} k="owed_to" numericKeys={NUMERIC_DEBT_KEYS}>Creditor</SortableTh>
                    <SortableTh sort={debtSort} k="who" numericKeys={NUMERIC_DEBT_KEYS}>Who</SortableTh>
                    <SortableTh sort={debtSort} k="status" numericKeys={NUMERIC_DEBT_KEYS}>Status</SortableTh>
                    <SortableTh sort={debtSort} k="debt_left" numericKeys={NUMERIC_DEBT_KEYS} align="right">Debt left</SortableTh>
                    <SortableTh sort={debtSort} k="planned_monthly_payment" numericKeys={NUMERIC_DEBT_KEYS} align="right">Planned payment</SortableTh>
                    <SortableTh sort={debtSort} k="average_monthly_interest" numericKeys={NUMERIC_DEBT_KEYS} align="right">Avg interest %</SortableTh>
                    <SortableTh sort={debtSort} k="interest_this_month" numericKeys={NUMERIC_DEBT_KEYS} align="right">Interest this month</SortableTh>
                    <SortableTh sort={debtSort} k="total_monthly_cost" numericKeys={NUMERIC_DEBT_KEYS} align="right">Monthly payment</SortableTh>
                  </tr>
                </thead>
                <tbody>
                  {sortedItems.map((row) => (
                    <tr key={row.debt_id} className="border-b border-[#EEEBE2] hover:bg-[#FAF8F2]/60" data-testid="plan-debt-row">
                      <td className="py-3 px-2">
                        <Link to={`/debts/${row.debt_id}`} className="font-mono text-xs text-[#2C4A3B] hover:text-[#1D3328] underline underline-offset-2">
                          {row.code}
                        </Link>
                      </td>
                      <td className="py-3 px-2 text-[#1D3328] font-medium">{row.owed_to}</td>
                      <td className="py-3 px-2 text-[#5C635A]">{row.who}</td>
                      <td className="py-3 px-2"><StatusBadge status={row.status} /></td>
                      <td className="py-3 px-2 text-right num text-[#E07A5F]">{formatEuro(row.debt_left)}</td>
                      <td className="py-3 px-2 text-right num text-[#2C4A3B]">{formatEuro(row.planned_monthly_payment)}</td>
                      <td className="py-3 px-2 text-right num text-[#5C635A]">
                        {(row.average_monthly_interest * 100).toFixed(2)}%
                      </td>
                      <td className="py-3 px-2 text-right num text-[#C89B6A]">{formatEuro(row.interest_this_month)}</td>
                      <td className="py-3 px-2 text-right num font-medium text-[#1D3328]">{formatEuro(row.total_monthly_cost)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-[#E5E3DB]">
                    <td colSpan={5} className="py-3 px-2 text-right label-kicker text-[#1D3328]">Total per month</td>
                    <td className="py-3 px-2 text-right num text-[#2C4A3B] font-medium">{formatEuro(totals.planned_payments || 0)}</td>
                    <td></td>
                    <td className="py-3 px-2 text-right num text-[#C89B6A] font-medium">{formatEuro(totals.interest_this_month || 0)}</td>
                    <td className="py-3 px-2 text-right num font-medium text-[#1D3328]" data-testid="plan-table-total-cost">
                      {formatEuro(totals.monthly_outflow || 0)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </section>

          {/* Breakdowns */}
          <section className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <BreakdownCard title="By person" rows={summary?.by_person || []} accent="#2C4A3B" testId="plan-by-person" />
            <BreakdownCard title="By creditor" rows={summary?.by_creditor || []} accent="#E07A5F" testId="plan-by-creditor" />
          </section>

          {/* Projection */}
          <section className="card-soft p-6">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
              <div>
                <p className="label-kicker">Forward look</p>
                <h3 className="font-display text-xl text-[#1D3328] mt-1 flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-[#C89B6A]" strokeWidth={1.7} />
                  Planned debt trajectory
                </h3>
                {projection?.all_cleared_month && (
                  <p className="text-xs text-[#5C635A] mt-1">
                    At this pace, every debt is cleared by{" "}
                    <span className="font-medium text-[#2C4A3B]">{formatMonthShort(projection.all_cleared_month)}</span>.
                  </p>
                )}
              </div>
              <Tabs value={months} onValueChange={setMonths}>
                <TabsList className="bg-[#EEEBE2]" data-testid="plan-range-tabs">
                  {RANGES.map((r) => (
                    <TabsTrigger
                      key={r.value}
                      value={r.value}
                      className="data-[state=active]:bg-white data-[state=active]:text-[#1D3328]"
                      data-testid={`plan-range-${r.value}`}
                    >
                      {r.label}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </Tabs>
            </div>
            {timeline.length === 0 ? (
              <p className="text-[#8B9389] text-sm">No projection data yet.</p>
            ) : (
              <>
                <div className="h-72 min-h-[18rem] w-full" data-testid="plan-debt-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={timeline} margin={{ top: 10, right: 20, bottom: 0, left: 0 }}>
                      <defs>
                        <linearGradient id="planGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#E07A5F" stopOpacity={0.35} />
                          <stop offset="100%" stopColor="#E07A5F" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#EEEBE2" />
                      <XAxis
                        dataKey="month"
                        tickFormatter={formatMonthShort}
                        tick={{ fontSize: 11, fill: "#8B9389" }}
                        interval={Math.max(0, Math.floor(timeline.length / 8))}
                      />
                      <YAxis tick={{ fontSize: 11, fill: "#8B9389" }} tickFormatter={(v) => `€${Math.round(v / 1000)}k`} />
                      <Tooltip
                        formatter={(v) => formatEuro(v)}
                        labelFormatter={(l) => formatMonthShort(l)}
                        contentStyle={{ background: "#FFF", border: "1px solid #E5E3DB", borderRadius: 12 }}
                      />
                      <ReferenceLine y={0} stroke="#C5CFC3" />
                      <Area type="monotone" dataKey="total_debt" name="Total debt" stroke="#E07A5F" strokeWidth={2.5} fill="url(#planGrad)" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>

                <div className="h-64 min-h-[16rem] w-full mt-6" data-testid="plan-flow-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={timeline.slice(1)} margin={{ top: 10, right: 20, bottom: 0, left: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#EEEBE2" />
                      <XAxis dataKey="month" tickFormatter={formatMonthShort} tick={{ fontSize: 11, fill: "#8B9389" }} />
                      <YAxis tick={{ fontSize: 11, fill: "#8B9389" }} tickFormatter={(v) => `€${Math.round(v)}`} />
                      <Tooltip
                        formatter={(v, name) => [formatEuro(v), name]}
                        labelFormatter={(l) => formatMonthShort(l)}
                        contentStyle={{ background: "#FFF", border: "1px solid #E5E3DB", borderRadius: 12 }}
                      />
                      <Legend />
                      <Bar dataKey="payment_this_month" name="Payment" fill="#2C4A3B" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="interest_this_month" name="Interest" fill="#C89B6A" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                <div className="overflow-x-auto -mx-2 mt-6">
                  <table className="w-full text-sm" data-testid="plan-projection-table">
                    <thead>
                      <tr className="text-left label-kicker border-b border-[#EEEBE2]">
                        <SortableTh sort={projSort} k="month" numericKeys={NUMERIC_PROJ_KEYS}>Month</SortableTh>
                        <SortableTh sort={projSort} k="payment_this_month" numericKeys={NUMERIC_PROJ_KEYS} align="right">Payment</SortableTh>
                        <SortableTh sort={projSort} k="interest_this_month" numericKeys={NUMERIC_PROJ_KEYS} align="right">Interest</SortableTh>
                        <SortableTh sort={projSort} k="net_change" numericKeys={NUMERIC_PROJ_KEYS} align="right">Net change</SortableTh>
                        <SortableTh sort={projSort} k="total_debt" numericKeys={NUMERIC_PROJ_KEYS} align="right">Debt remaining</SortableTh>
                        <SortableTh sort={projSort} k="active_debts" numericKeys={NUMERIC_PROJ_KEYS} align="right">Active debts</SortableTh>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedProjection.map((row) => (
                        <tr key={row.month_index} className="border-b border-[#EEEBE2] hover:bg-[#FAF8F2]/60" data-testid="plan-projection-row">
                          <td className="py-3 px-2 text-[#1D3328] font-medium">{formatMonthShort(row.month)}</td>
                          <td className="py-3 px-2 text-right num text-[#2C4A3B]">−{formatEuro(row.payment_this_month)}</td>
                          <td className="py-3 px-2 text-right num text-[#C89B6A]">+{formatEuro(row.interest_this_month)}</td>
                          <td
                            className="py-3 px-2 text-right num"
                            style={{ color: row.net_change <= 0 ? "#2C4A3B" : "#B04A32" }}
                          >
                            {row.net_change >= 0 ? "+" : ""}{formatEuro(row.net_change)}
                          </td>
                          <td className="py-3 px-2 text-right num font-medium text-[#E07A5F]">{formatEuro(row.total_debt)}</td>
                          <td className="py-3 px-2 text-right num text-[#5C635A]">{row.active_debts}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </section>
        </>
      )}
    </div>
  );
}

function SortableTh({ children, sort, k, numericKeys, align = "left" }) {
  const active = sort.sortBy === k;
  const Icon = sort.sortDir === "asc" ? ArrowUp : ArrowDown;
  return (
    <th
      className={`py-3 px-2 ${align === "right" ? "text-right" : "text-left"} cursor-pointer select-none group whitespace-nowrap`}
      onClick={() => sort.toggle(k, numericKeys)}
      data-testid={`plan-sort-${k}`}
      aria-sort={active ? (sort.sortDir === "asc" ? "ascending" : "descending") : "none"}
    >
      <span
        className={`inline-flex items-center gap-1 transition-colors ${
          active ? "text-[#1D3328]" : "text-[#8B9389] group-hover:text-[#5C635A]"
        }`}
      >
        {children}
        {active ? (
          <Icon className="w-3 h-3" strokeWidth={2.2} />
        ) : (
          <ArrowDown className="w-3 h-3 opacity-0 group-hover:opacity-30" strokeWidth={2} />
        )}
      </span>
    </th>
  );
}

function Tile({ icon, label, value, sub, accent, testId }) {
  return (
    <div className="card-soft p-6" data-testid={testId}>
      <div className="flex items-center justify-between">
        <p className="label-kicker">{label}</p>
        <div
          className="w-8 h-8 rounded-lg flex items-center justify-center"
          style={{ background: `${accent}18`, color: accent }}
        >
          {icon}
        </div>
      </div>
      <div className="font-display text-2xl font-medium num mt-3" style={{ color: accent }}>{value}</div>
      <div className="text-xs text-[#8B9389] mt-1">{sub}</div>
    </div>
  );
}

function BreakdownCard({ title, rows, accent, testId }) {
  const max = useMemo(() => Math.max(...rows.map((r) => r.amount), 1), [rows]);
  return (
    <div className="card-soft p-6" data-testid={testId}>
      <p className="label-kicker">{title}</p>
      <h3 className="font-display text-xl text-[#1D3328] mt-1 mb-4">Monthly cost share</h3>
      {rows.length === 0 ? (
        <p className="text-[#8B9389] text-sm">Nothing to break down yet.</p>
      ) : (
        <ul className="space-y-3">
          {rows.map((r) => (
            <li key={r.name}>
              <div className="flex items-center justify-between text-sm">
                <span className="text-[#1D3328] font-medium">{r.name}</span>
                <span className="num text-[#1D3328]">{formatEuro(r.amount)}</span>
              </div>
              <div className="h-2 bg-[#EEEBE2] rounded-full overflow-hidden mt-1.5">
                <div
                  className="h-full rounded-full transition-all"
                  style={{ width: `${(r.amount / max) * 100}%`, background: accent }}
                />
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
